import { HttpApiService } from "@/shared/services/httpApi.service";

import type {
  AmountLimitsResponse,
  ClearAmountLimitScope,
  UpsertAmountLimitPayload,
} from "../types/paymentLimits.types";

/**
 * Per-transaction amount limits — coloca-nest `InternalPaymentLimitsController`. Every endpoint is
 * `userTypes: [INTERNAL]`:
 *
 * - `GET /internal/payments/limits?organization_id=` (`payments:read`) — the matrix over the active corridors.
 * - `PUT /internal/payments/limits/:rail` (`payments:write`) — atomic upsert of one exact scope; the backend
 *   validates the bounds (at least one, positive, at the currency precision, min < max) and stamps the
 *   author. Superseded rows stay as the audit trail.
 * - `DELETE /internal/payments/limits/:rail?direction=&country=&currency=&payer_type=&organization_id=`
 *   (`payments:write`) — removes one exact scope so it follows the wider one (or becomes unbounded) again;
 *   404 `rail_limit_not_found` when no live row matches.
 *
 * A change takes effect immediately on the serving instance and within 60 s on the rest; both corridor
 * catalogs publish the new bounds under `limits`.
 */

const LIMITS_URL = "/internal/payments/limits";

const toScopeQuery = (scope: ClearAmountLimitScope): string => {
  const params = new URLSearchParams();
  params.set("direction", scope.direction);
  params.set("country", scope.country);
  params.set("currency", scope.currency);
  if (scope.payer_type) params.set("payer_type", scope.payer_type);
  if (scope.organization_id) params.set("organization_id", scope.organization_id);
  return `?${params.toString()}`;
};

export const PaymentLimitsService = {
  async list(organizationId?: string | null): Promise<AmountLimitsResponse> {
    const query = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
    const { data } = await HttpApiService.get<AmountLimitsResponse>(`${LIMITS_URL}${query}`);
    return data;
  },

  async upsert(rail: string, payload: UpsertAmountLimitPayload): Promise<void> {
    await HttpApiService.put(`${LIMITS_URL}/${encodeURIComponent(rail)}`, payload);
  },

  async clear(rail: string, scope: ClearAmountLimitScope): Promise<void> {
    await HttpApiService.delete(`${LIMITS_URL}/${encodeURIComponent(rail)}${toScopeQuery(scope)}`);
  },
};
